import { type AxiosError } from 'axios'
import React, { createContext, useContext, useMemo } from 'react'
import { type UseQueryResult, type UseQueryOptions } from 'react-query'

import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { formatExistingStatusCheck } from 'utils/team-external-integrations/team-external-integrations-util'

import type {
  ServiceStatusCheckFromStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'

export type StatusChecksContextValues = UseQueryResult<
  StatusCheck[],
  AxiosError
> & {
  statusChecks: ServiceStatusCheckFromStatusCheck[]
  statusChecksRaw: StatusCheck[]
}

export const StatusChecksContext = createContext<
  StatusChecksContextValues | undefined
>(undefined)

export const StatusChecksProvider: React.FC<{
  queryOptions?: UseQueryOptions<StatusCheck[]>
}> = ({ children, queryOptions }) => {
  const statusCheckQueryResponse = useGremlinQuery<StatusCheck[]>(
    '/status-checks',
    {},
    { placeholderData: [], ...queryOptions }
  )

  const values: StatusChecksContextValues = useMemo(() => {
    const { data } = statusCheckQueryResponse
    // The "statusChecks" value provided here is actually a list of type ServiceStatusCheck, not StatusCheck. This is a
    // legacy issue and should be refactored to use the correct type. In order to continue using the correct type, we
    // surface the "statusChecksRaw" value, which is a list of type StatusCheck.
    // TODO: Refactor components that use these values to use the correct type
    // TODO: EN-6581
    const statusChecks =
      data && !statusCheckQueryResponse.isError
        ? data.map((s) => formatExistingStatusCheck(s))
        : []

    return {
      ...statusCheckQueryResponse,
      statusChecksRaw: data ?? [],
      statusChecks
    }
  }, [statusCheckQueryResponse])

  return (
    <StatusChecksContext.Provider value={values}>
      {children}
    </StatusChecksContext.Provider>
  )
}

export function useStatusChecksContext() {
  const context = useContext(StatusChecksContext)

  if (!context) {
    throw new Error(
      'useStatusChecksContext must be used within a StatusChecksProvider'
    )
  }

  return context
}
